import api, { ApiMethod, ApiMethodDto, ApiMethodRes } from '../../api';
import { AxiosError } from 'axios';
import { useState, useCallback } from 'react';
import { queryCache, useMutation, useQuery } from 'react-query-1';
import useErrors from './errors.hooks';

export function useAPI<K extends ApiMethod>(
  path: K,
  data: ApiMethodDto<K> | (() => ApiMethodDto<K>),
  options?: {
    refetchOnFocus?: boolean;
    ready?: boolean;
    retry?: boolean;
    onError?: (err: AxiosError) => void;
  },
): {
  loading: boolean;
  refreshing: boolean;
  refresh: () => Promise<void>;
  data: ApiMethodRes<K> | undefined;
} {
  const { refetchOnFocus, onError, retry } = options ?? {};

  const handleErr = useErrors();
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // @ts-ignore
  const getData = () => (typeof data === 'function' ? data() : data);

  const query = useQuery(
    options?.ready ?? true ? [path, getData()] : null,
    (path, data) => api.request<K>(path, data),
    {
      onError: (err) => {
        if (onError) {
          onError(err as AxiosError);
        } else {
          handleErr(err);
        }
      },
      retry: retry ?? true,
      retryDelay: () => 2000,
      refetchOnWindowFocus: refetchOnFocus ?? true,
    },
  );

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await query.refetch({ force: true, throwOnError: true });
    setRefreshing(false);
  }, [query]);

  const loading = query.status === 'loading' || !query.data;

  return {
    loading,
    // refreshing is true only if the query is fetching because of a manual refresh
    refreshing,
    refresh,
    data: query.data,
  };
}

export function useAPIMutation<K extends ApiMethod>(
  path: K,
  {
    queries,
    onSuccess,
    onError,
  }: {
    queries?: ReadonlyArray<ApiMethod | [ApiMethod, ApiMethodDto<ApiMethod>]>;
    onSuccess?: (res: ApiMethodRes<K>) => void;
    onError?: (err: AxiosError) => void;
  },
): [(data: ApiMethodDto<K>) => Promise<ApiMethodRes<K>>, { loading: boolean }] {
  const handleErr = useErrors();

  const [mutate, { status }] = useMutation((data: ApiMethodDto<K>) => api.request(path, data), {
    onSuccess: (res) => {
      for (const query of queries ?? []) {
        queryCache.refetchQueries(query);
      }

      if (onSuccess) onSuccess(res);
    },
    onError: (err) => {
      if (onError) {
        onError(err as AxiosError);
      } else {
        handleErr(err);
      }
    },
  });

  return [mutate, { loading: status === 'loading' }];
}
